<template>
  <div>
    <v-row class="mx-2 py-1 align-center">
      <v-col>
        <v-text-field v-model="table.search" append-icon="mdi-magnify" :label="$t('general.misc.search')" clearable hide-details></v-text-field>
      </v-col>
      <v-col>
        <v-autocomplete :label="$t('general.armorClass.title')" v-model="armorClass" :items="armorClasses" return-object clearable
                    item-value="value" item-text="text" hide-details></v-autocomplete>
      </v-col>
      <v-col>
        <v-autocomplete :label="$t('general.misc.difficulty')" v-model="difficulty" :items="difficulties" return-object clearable
                        item-value="value" item-text="text" hide-details></v-autocomplete>
      </v-col>
    </v-row>

    <v-row class="mx-2 my-1 py-1 align-center">
      <v-checkbox v-model="showDetails" :label="$t('loot.show_details')" class="ml-3 mr-2"></v-checkbox>
      <v-spacer></v-spacer>
      <v-btn @click="startLootSession" class="green mr-2" v-if="!lootMasterSessionActive && manageLoot && lootSession === null">
        <v-icon class="mr-1">mdi-sack</v-icon>
        {{ $t('loot.sessions.start_session') }}
      </v-btn>
      <v-btn @click="stopLootSession" class="red mr-2" v-if="lootMasterSessionActive">
        <v-icon class="mr-1">mdi-stop</v-icon>
         {{ $t('loot.sessions.stop_session') }}
      </v-btn>
      <v-btn @click="leaveLootSession" class="red mr-2"  v-if="!lootMasterSessionActive && lootSession !== null">
        <v-icon class="mr-1">mdi-link-off</v-icon>
         {{ $t('loot.sessions.leave_session') }}
      </v-btn>
      <v-btn @click="expandAll" class="mr-2">
        <v-icon class="mr-1">mdi-expand-all</v-icon>
        {{ $t('general.actions.expand') }}
      </v-btn>
      <v-btn @click="collapseAll" class="mr-2">
        <v-icon class="mr-1">mdi-collapse-all</v-icon>
        {{ $t('general.actions.collapse') }}
      </v-btn>
    </v-row>

    <v-card class="secondary lighten-1 pa-0 mb-2" v-if="!lootMasterSessionActive && lootSessions.length > 0 && lootSession === null">
      <v-card-text>
        <v-card-subtitle>
          <h3 class="mb-4">{{ $t('loot.sessions.open_sessions') }}</h3>
          <v-list two-line class="ma-0 pa-0">
            <v-list-item class="priest-bg" v-for="session in filteredLootSessions" :key="session.id" >
              <v-list-item-content>
                <v-list-item-title>
                  <b>{{ $t('loot.loot_master') }}: {{session.lootMaster.display}}</b>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <b>{{ $t('general.timestamps.created_at') }}:</b> {{parseDate(session.createdAt)}} - <b>{{ $t('loot.sessions.session_time') }}:</b> {{ formattedSessionTime(session) }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon @click="joinLootSession(session)"  v-bind="attrs" v-on="on">
                      <v-icon>mdi-link</v-icon>
                    </v-btn>
                  </template>
                  <span> {{ $t('loot.sessions.join_session') }} </span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </v-card-subtitle>
      </v-card-text>
    </v-card>
    <v-card class="secondary lighten-1 pa-0 mb-2" v-if="lootSession != null">
      <v-card-text>
        <v-card-subtitle>
          <v-list two-line class="ma-0 pa-0">
            <v-list-item class="priest-bg">
              <v-list-item-content>
                <v-list-item-title>
                  <h3 class="mb-4">{{ $t('loot.sessions.current_session') }}</h3>
                </v-list-item-title>
                <v-list-item-subtitle>
                  <p class="mb-0 pb-0"><b>{{ $t('loot.loot_master') }}:</b> {{lootSession?.lootMaster.display}}</p>
                  <p class="mb-0 pb-0"><b>{{ $t('general.timestamps.created_at') }}:</b> {{ parseDate(lootSession?.createdAt) }}</p>
                  <p class="mb-0 pb-0"><b>{{ $t('general.timestamps.updated_at') }}:</b> {{ parseDate(lootSession?.updatedAt) }}</p>
                  <p class="mb-0 pb-0"><b>{{ $t('loot.sessions.session_time') }}:</b> {{ this.formattedSessionTime(this.lootSession) }}</p>
                </v-list-item-subtitle>

                <v-row class="pl-3 mt-2 align-center" v-if="lootMasterSessionActive">
                  <v-autocomplete :items="classList" v-model="temporaryLootEntry.class" :label=" $t('general.misc.class')" return-object clearable
                                  :filter="Utilities().searchNormalizedClass" class="ml-3 mr-2" style="max-width: 400px;" >
                    <template v-slot:selection="{ item }">
                      <p class="font-weight-medium mb-0">
                        <class-avatar :size="18" :type="'class'" :value="item?.toLowerCase()" class="mr-1"></class-avatar>
                        <span :class="item?.toLowerCase().replaceAll(' ', '-')">{{ classMap[item][$i18n.locale] }}</span>
                      </p>
                    </template>
                    <template v-slot:item="{ item }">
                      <p class="font-weight-medium mb-0">
                        <class-avatar :size="18" :type="'class'" :value="item?.toLowerCase()" class="mr-1"></class-avatar>
                        <span :class="item?.toLowerCase().replaceAll(' ', '-')">{{ classMap[item][$i18n.locale] }}</span>
                      </p>
                    </template>
                  </v-autocomplete>
                  <v-text-field :label=" $t('loot.temporary_character')" class="mr-2" v-model="temporaryLootEntry.name" clearable
                                style="max-width: 400px;"></v-text-field>
                  <v-btn text class="ml-2" @click="addTemporaryLootEntry"
                         :disabled="temporaryLootEntry.name === '' || temporaryLootEntry.name === null || temporaryLootEntry.class === null">
                    <v-icon class="mr-1">mdi-plus-circle</v-icon>
                    {{ $t('general.actions.add') }}
                  </v-btn>
                </v-row>
              </v-list-item-content>
            </v-list-item>
          </v-list>

        </v-card-subtitle>

      </v-card-text>
    </v-card>

    <v-row class="pa-2 mr-1 justify-end">
      <p v-if="lootMasterSessionActive"><b>{{ $t("loot.max_raid_count") }}: {{ maxRaidCount + 1 }}</b></p>
      <p v-else><b>{{ $t("loot.max_raid_count") }}: {{ maxRaidCount }}</b></p>
    </v-row>
    <v-data-table :headers="showHeaders" :items="filterLootEntries" item-key="id" :custom-filter="Utilities().searchNormalized"
                  :search="table.search" :loading="isLoading"
                  show-expand :expanded.sync="table.expanded"
                  :page.sync="table.page" :items-per-page="table.itemsPerPage" hide-default-footer
                  @page-count="table.pageCount = $event" multi-sort>
      <template v-slot:[`header.${head.value}`]=" { }" v-for="head in showHeaders">
        <v-tooltip top v-if="head.tooltip" v-bind:key="head.value">
          <template v-slot:activator="{ on, attrs }">
            {{ head.text }}
            <v-icon medium class="ml-2 mb-1" v-bind="attrs" v-on="on">mdi-information</v-icon>
          </template>
          <span>{{ head.tooltip }}</span>
        </v-tooltip>
        <span v-else v-bind:key="head.value">{{ head.text }}</span>
      </template>
      <template v-slot:item.class="{ item }">
        <class-avatar v-bind:type="'class'" v-bind:value="item.class.toLowerCase()" v-bind:size="30"/>
      </template>
      <template v-slot:item.name="{ item }">
        <span :class="item.class.toLowerCase().replaceAll(' ', '-')"><b>{{ item.name }}</b></span>
        <v-icon v-if="lootMasterSessionActive && item.isTemporary" @click="removeTemporaryLootEntry(item)">mdi-delete</v-icon>
      </template>
      <template v-slot:item.earliestRaidDate="{ item }">
        {{ item.earliestRaidDate || "-" }}
      </template>
      <template v-slot:item.attendancePercent="{ item }">
        {{ item.attendancePercent }} %
      </template>
      <template v-slot:item.attendancePercentEdit="{ item }">
        {{ item.attendancePercentEdit }} %
      </template>
      <template v-slot:item.sessionItemCount="{ item }">
        <v-icon @click="changeSessionItemCount(item.id, -1);" v-if="lootMasterSessionActive"
                :disabled="item.sessionItemCount === 0" class="mr-2">mdi-minus-circle
        </v-icon>
        <b class="primary--text">{{ item.sessionItemCount }}</b>
        <v-icon @click="changeSessionItemCount(item.id, +1);" class="ml-2" v-if="lootMasterSessionActive">mdi-plus-circle</v-icon>
      </template>
      <template v-slot:item.itemsCount="{ item }">
        <b class="primary--text">{{ item.itemsCount }}</b>
      </template>
      <template #item.data-table-expand="{ item, expand, isExpanded }">
        <td class="text-start">
          <v-btn icon
                 @click="expand(!isExpanded)"
                 class="v-data-table__expand-icon"
                 :class="{'v-data-table__expand-icon--active' : isExpanded}">
            <v-icon>mdi-chevron-down</v-icon>
          </v-btn>
        </td>
      </template>
      <template v-slot:expanded-item="{ item }">
        <td :colspan="table.headers.length" class="pa-3">
          <loot-detail-view :loot-entry="item" :difficulty="difficulty"></loot-detail-view>
        </td>
      </template>
    </v-data-table>
    <div class="text-center pt-2">
      <v-pagination v-if="lootEntries.length > 0" v-model="table.page" :length="table.pageCount"></v-pagination>
    </div>
  </div>
</template>

<script>
import MessageService from "@/services/message.service";
import ClassAvatar from "@/components/ClassAvatar";
import CharacterService from "@/services/character.service";
import LootDetailView from "@/components/LootDetailView.vue";
import Utilities from "@/util/Utilities";
import {io} from "socket.io-client";

export default {
  name: "LootTable",
  props: ["roster", "useExtraItems"],
  components: {LootDetailView, ClassAvatar},
  data() {
    return {
      isLoading: true,
      socket: null,
      intervalId: null,
      now: new Date(),
      lootSessions: [],
      lootSession: null,
      lootMasterSessionActive: false,
      lootEntries: [],
      originLootEntries: [],
      temporaryLootEntry: {
        name: "",
        class: "Death Knight"
      },
      showDetails: false,
      difficulties: [],
      difficulty: null,
      armorClass: null,
      armorClasses: [
        {value: "cloth", text: this.$t('general.armorClass.cloth')},
        {value: "leather", text: this.$t('general.armorClass.leather')},
        {value: "mail", text: this.$t('general.armorClass.mail')},
        {value: "plate", text: this.$t('general.armorClass.plate')}
      ],
      maxRaidCount: 0,
      classList: this.$store.getters["data/classInfo"].list.sort(),
      classMap: this.$store.getters["data/classInfo"].map,

      table: {
        page: 1,
        pageCount: 0,
        itemsPerPage: 40,
        expanded: [],
        search: '',
        headers: [
          {
            text: '', value: "searchKeys", align: " d-none", simple: true
          },
          {
            text: '', value: "class", width: "20px", simple: true,
            filter: value => this.filterOnArmoryClass(value)
          },
          {text: this.$t('general.misc.name'), value: "name", simple: true},
          {text: this.$t('loot.table.state'), value: "state", simple: true},
          {text: this.$t('general.misc.default_role'), value: "defaultRole", align: " d-none", simple: true},
          {text: this.$t('loot.table.earliest_raid'), value: "earliestRaidDate", align: 'center'},
          {text: this.$t('loot.table.attendance'), value: "attendanceCount", align: 'center', simple: true, lootMasterMode: false},
          {text: this.$t('loot.table.attendance'), value: "attendanceCountEdit", align: 'center', simple: true, lootMasterMode: true},
          {text: this.$t('loot.table.attendance_percent'), value: "attendancePercent", align: 'center', simple: true, lootMasterMode: false},
          {text: this.$t('loot.table.attendance_percent'), value: "attendancePercentEdit", align: 'center', simple: true, lootMasterMode: true},
          {text: this.$t('loot.table.bonus'), value: "bonusCount", align: this.useExtraItems ? 'center' : " d-none"},
          {text: this.$t('loot.table.penalty'), value: "penaltyCount", align: this.useExtraItems ? 'center' : " d-none"},
          {text: this.$t('loot.table.items_per_session'), value: "sessionItemCount", align: 'center', simple: true, lootMasterMode: true},
          {text: this.$t('loot.table.items'), value: "itemsCount", align: 'center', simple: true},
          {text: '', value: 'data-table-expand', simple: true}
        ],
      }
    }
  },
  mounted() {
    this.intervalId = setInterval(() => {
      this.now = new Date();
    }, 1000);

    this.loadLootEntryData().then(() => {
      this.socketLogic();
    })

    let appConfig = this.$store.getters["data/appConfig"];
    let difficulties = appConfig.find(x => x.key === "DIFFICULTIES");
    let currentDifficulty = appConfig.find(x => x.key === `DEFAULT_DIFFICULTY_${this.roster.toUpperCase()}`);
    console.log(`Difficulty - ${this.roster} - ${currentDifficulty?.value}`);
    if (difficulties && currentDifficulty) {
      this.difficulties = difficulties.value.split(";");
      this.difficulty = currentDifficulty.value;

      if (this.difficulty === null) {
        this.difficulty = this.difficulties[0];
      }
    }

    this.$root.$on(`raidUpdate_${this.roster}`, () => {
      this.loadLootEntryData();
    });
  },
  beforeDestroy() {
    this.$root.$off(`raidUpdate_${this.roster}`);
  },
  methods: {
    socketLogic() {
      this.socket = io(`${process.env.VUE_APP_BACKEND_URL}:${process.env.VUE_APP_BACKEND_PORT}`);
      if (this.socket) {
        this.socket.on('loot_session:info', res => {
          this.lootSessions = Object.values(res);
          if(this.manageLoot && this.lootSessions.length > 0){
            //search for loot session of current user
            let session = this.lootSessions.find(x => x.lootMaster.id === this.currentUser.data.id && x.roster === this.roster);
            if(session){
              this.lootSession = session;
              this.lootMasterSessionActive = true;
              this.lootEntries = JSON.parse(JSON.stringify(this.lootSession.lootEntries));
            }
          }
        });

        this.socket.on('loot_session:started', res => {
          this.lootSessions.push(res);
        });

        this.socket.on('loot_session:updated', res => {
          let index = this.lootSessions.findIndex(x => x.identifier === res.identifier);
          if(index !== -1){
            this.lootSessions[index] = res;
          }

          if(this.lootSession){
            this.lootSession.updatedAt = res.updatedAt;
            this.lootEntries = JSON.parse(JSON.stringify(res.lootEntries));
          }
        });

        this.socket.on('loot_session:stopped', res => {
          let index = this.lootSessions.findIndex(x => x.identifier === res);

          let lootMaster = -1;
          if(index !== -1){
            lootMaster = this.lootSessions[index].lootMaster.id;
            this.lootSessions.splice(index, 1);
          }

          if(this.lootSession && this.lootSession.identifier === res){
            this.lootSession = null;

            if(this.currentUser.data.id !== lootMaster){
              MessageService.info("Your loot session has been stopped by the loot master.");
            }
          }
        });

        this.socket.on('loot_session:session_info', res => {
          if(res.found && this.lootSession.identifier === res.session.identifier){
            this.lootSession.updatedAt = res.session.updatedAt;
            this.lootEntries = JSON.parse(JSON.stringify(res.session.lootEntries));
          }
        });
      } else {
        MessageService.error("Socket connection for loot sessions could not be established");
      }
    },
    Utilities() {
      return Utilities
    },
    expandAll() {
      this.table.expanded = this.lootEntries;
    },
    collapseAll() {
      this.table.expanded = [];
    },
    filterOnArmoryClass(value) {
      let armoryMap = {
        cloth: ["Mage", "Priest", "Warlock"],
        leather: ["Demon Hunter", "Druid", "Monk", "Rogue"],
        mail: ["Evoker", "Hunter", "Shaman"],
        plate: ["Death Knight", "Paladin", "Warrior"],
      };

      if (this.armorClass === null) {
        return true;
      } else {
        return armoryMap[this.armorClass.value].includes(value);
      }
    },
    startLootSession(){
      let timestamp = new Date(Date.now());

      let lootEntries = JSON.parse(JSON.stringify(this.originLootEntries));
      lootEntries.forEach(lootEntry => {
        this.calculateLootMasterValues(lootEntry);
      });

      this.lootSession = {
        identifier: `loot_session_${this.roster}_${this.currentUser.data.id}`,
        lootMaster: {
          id: this.currentUser.data.id,
          display: this.currentUser.data.username
        },
        roster: this.roster,
        createdAt: timestamp.getTime(),
        updatedAt: timestamp.getTime(),
        lootEntries: lootEntries
      }

      this.lootEntries = lootEntries;
      this.socket.emit('loot_session:start', this.lootSession);
      this.lootMasterSessionActive = true;
    },
    stopLootSession(){
      this.socket.emit('loot_session:stop', {
        identifier: this.lootSession.identifier
      });
      this.lootMasterSessionActive = false;
    },
    joinLootSession(session){
      this.lootSession = session;
      this.lootEntries = JSON.parse(JSON.stringify(session.lootEntries));
      this.socket.emit('loot_session:session_info_request', {
        identifier: session.identifier
      });
    },
    leaveLootSession(){
      this.lootSession = null;
      this.lootEntries = JSON.parse(JSON.stringify(this.originLootEntries));
    },
    loadLootEntryData() {
      this.isLoading = true;
      return new Promise(resolve => {
        CharacterService.getListForLoot(this.roster).then(result => {
          if (result) {
            this.maxRaidCount = result["raidCount"];
            this.originLootEntries = result["lootEntries"].map(lootEntry => ({
              ...lootEntry,
              searchKeys: lootEntry.subEntries.map(x => x.name),
              isTemporary: false,
              sessionItemCount: 0
            }));

            this.lootEntries = JSON.parse(JSON.stringify(this.originLootEntries));
            this.isLoading = false;
            resolve();
          } else {
            this.isLoading = false;
            resolve();
          }
        }).catch(() => {
          this.isLoading = false;
          resolve();
        })
      });
    },
    calculateLootMasterValues(lootEntry) {
      let attendanceCount = lootEntry.attendanceCount + 1;
      let attendanceRelative = attendanceCount / (lootEntry.totalRaidCount + 1);

      lootEntry.attendanceCountEdit = attendanceCount;
      lootEntry.attendancePercentEdit = Number((attendanceRelative * 100).toFixed(0));
      lootEntry.sessionItemCount = 0;

    },
    changeSessionItemCount(entryId, delta) {
      const entry = this.lootEntries.find(e => e.id === entryId);
      if (!entry) return;

      const next = (Number(entry.sessionItemCount) || 0) + delta;
      entry.sessionItemCount = Math.max(0, next);

      // optional: auch gleich im lootSession-Objekt halten (falls du das nutzt)
      if (this.lootSession?.lootEntries) {
        const sEntry = this.lootSession.lootEntries.find(e => e.id === entryId);
        if (sEntry) sEntry.sessionItemCount = entry.sessionItemCount;
      }

      this.updateLootSession(entry, delta > 0 ? "+" : "-");
    },
    updateLootSession(item, action) {
      //call within session -> change items for entry
      //send update to socket
      this.socket.emit('loot_session:update', {
        identifier: this.lootSession.identifier,
        lootEntries: this.lootEntries,
        description: `Character: '${item.name}' | Action: '${action}' | Session Item Count: '${item.sessionItemCount}'`
      });
    },
    addTemporaryLootEntry() {
      if (this.temporaryLootEntry.name && this.temporaryLootEntry.name.length > 0 && this.temporaryLootEntry.class && this.temporaryLootEntry.class.length > 0) {
        if (this.classList.includes(this.temporaryLootEntry.class)) {
          if (!this.lootEntries.find(x => x.name === this.temporaryLootEntry.name)) {
            let lootEntry = {
              id: `temporary_${this.temporaryLootEntry.name}`,
              isTemporary: true,
              class: this.temporaryLootEntry.class,
              name: this.temporaryLootEntry.name,
              rosterInformation: {
                [this.roster]: `temporary_${this.temporaryLootEntry.name}`
              },
              earliestRaidDate: new Date(Date.now()).toLocaleDateString(),
              state: 'Temporary',
              totalRaidCount: 0,
              attendanceCount: 0,
              itemsCount: 0,
              bonusCount: 0,
              penaltyCount: 0,
              sessionItemCount: 0,
              items: [],
              subEntries: []
            };
            this.calculateLootMasterValues(lootEntry);

            this.lootEntries.push(lootEntry);
          } else {
            MessageService.warn("Temporary character with this name already exists.");
          }
        } else {
          MessageService.warn("Please select a valid class.");
        }
      } else {
        MessageService.warn("Please specify an character to add temporary. Both fields must be filled.");
      }
    },
    removeTemporaryLootEntry(item) {
      let indexChars = this.lootEntries.indexOf(item);
      if (indexChars !== -1) {
        this.lootEntries.splice(indexChars, 1);
      }
    },
    parseDate(date){
      if(date){
        let dateParsed = new Date(date);
        if(dateParsed){
          return `${dateParsed.toLocaleDateString()} ${dateParsed.toLocaleTimeString()}`;
        }
      }

      return "";
    },
    sessionTime(session){
      const start = new Date(session?.createdAt);
      const now = this.now;

      if (isNaN(start.getTime()) || isNaN(now.getTime())) {
        return 0;
      }

      return Math.floor((now - start) / 1000);
    },
    formattedSessionTime(session) {
      const allSeconds = this.sessionTime(session);
      const hours = String(Math.floor(allSeconds / 3600)).padStart(1, '0')
      const minutes = String(Math.floor((allSeconds % 3600) / 60)).padStart(1, '0')
      const seconds = String(allSeconds % 60).padStart(1, '0')
      return `${hours}h ${minutes}min ${seconds}sec`;
    },
    filterItemsOnDifficultyAndCountable(items = []) {
      let countableItems = items.filter(it => it.countableItem);
      if (!this.difficulty) return countableItems;
      return countableItems.filter(it => it.difficulty === this.difficulty);
    }
  },
  computed: {
    currentUser() {
      return this.$store.getters["auth/user"];
    },
    manageLoot() {
      return this.currentUser && this.currentUser.rights?.includes("EDIT_LOOT_DATA");
    },
    showHeaders() {
      let headers;

      if (this.showDetails) {
        headers = this.table.headers.filter(x => x.extended === undefined || x.extended === true);
      } else {
        headers = this.table.headers.filter(x => x.simple);
      }

      if (this.lootSession != null) {
        return headers.filter(x => x.lootMasterMode === undefined || x.lootMasterMode === true);
      } else {
        return headers.filter(x => x.lootMasterMode === undefined || x.lootMasterMode === false);
      }
    },
    filterLootEntries() {
      let lootEntries = this.lootMasterSessionActive
          ? this.lootEntries
          : this.lootEntries.filter(x => x.isTemporary === false);

      return lootEntries.map(r => {
        const filteredItems = this.filterItemsOnDifficultyAndCountable(r.items);
        const baseCount = filteredItems.length;

        // Session items only count if they belong to the current difficulty.
        const sessionCount = Number(r.sessionItemCount) || 0;
        const sessionCountFiltered = this.difficulty ? sessionCount : sessionCount;
        const itemsCount = baseCount + sessionCountFiltered;

        return {
          ...r,
          itemsCount
        };
      });
    },
    filteredLootSessions(){
      return this.lootSessions.filter(x => x.roster === this.roster);
    }
  },
  unmounted(){
    clearInterval(this.intervalId);
  },
  watch: {
    '$i18n.locale': function () {
      let currentVal = this.armorClass.value;
      this.armorClass = {value: currentVal, text: this.$t(`general.armorClass.${currentVal}`)}
      this.armorClasses = [
        {value: "all", text: this.$t('general.armorClass.all')},
        {value: "cloth", text: this.$t('general.armorClass.cloth')},
        {value: "leather", text: this.$t('general.armorClass.leather')},
        {value: "mail", text: this.$t('general.armorClass.mail')},
        {value: "plate", text: this.$t('general.armorClass.plate')}
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.upgrade.huge {
  color: orangered;
}

.upgrade.big {
  color: orange;
}

.upgrade.small {
  color: greenyellow;
}

.upgrade.tiny {
  color: grey;
}
</style>